import { computed, ref, onMounted, type Ref } from "vue";
import { FormModes } from "@/models/form";
import { useForm } from "vee-validate";
import { AddressCountryIds, AddressTypeIds } from "@/config/constants";
import useValidation from "@/use/validation";
import useAddressFormData from "@/composables/forms/useAddressFormData";
import useAddressFormActions from "@/composables/forms/useAddressFormActions";
import type { FieldSchema, FormSchema } from "@/models/form";
import type { LOSType } from "@/models/opal";
import type { Address } from "@/models/application";
import * as yup from "yup";

export interface ValidationData {
  address: Ref<Record<any, any>>;
  initialValues?: Ref<Record<any, any>>;
  mode: Ref<keyof typeof FormModes>;
}

export default function useStudentAddressForm(data: ValidationData) {
  const addressBackup = ref<Address | undefined>(undefined);
  const prefixes = ref<string[]>([]);

  const { getOptionLabel, getErrorMessage, validZipFormat1Regex, validZipFormat2Regex } =
    useValidation();

  const { addressUnitTypes, countryOptions, statesOptions, unitTypeOptions } = useAddressFormData();

  const {
    formatAddress2,
    formatZipCode,
    getSelectedUnitType,
    getSelectedUnitTypeName,
    removePrefix,
  } = useAddressFormActions();

  const addressSchema = ref<FormSchema>({
    address1: {
      name: "address1",
      type: "text",
      subtype: "address",
      label: "Permanent Street Address",
      placeholder: "Street",
      required: true,
      maxLength: 50,
      showRequiredIndicator: true,
      validate: (schema: FieldSchema) => {
        schema.errors = errors.value.address1 ? [errors.value.address1] : [];
        schema.valid = schema.errors.length === 0;
        schema.touched = schema.errors.length > 0;
      },
    } as FieldSchema,
    addressUnitTypeId: {
      name: "addressUnitTypeId",
      type: "select",
      subtype: "address",
      label: "Unit Type",
      required: false,
      showRequiredIndicator: true,
      validate: (schema: FieldSchema) => {
        schema.errors = errors.value?.addressUnitTypeId ? [errors.value.addressUnitTypeId] : [];
        schema.valid = schema.errors.length === 0;
        schema.touched = schema.errors.length > 0;
      },
    } as FieldSchema,
    address2: {
      name: "address2",
      type: "text",
      subtype: "address",
      label: "Unit Number",
      placeholder: "Unit #",
      required: false,
      maxLength: 25,
      showRequiredIndicator: true,
      validate: (schema: FieldSchema) => {
        schema.errors = errors.value?.address2 ? [errors.value.address2] : [];
        schema.valid = schema.errors.length === 0;
        schema.touched = schema.errors.length > 0;
      },
    } as FieldSchema,
    city: {
      name: "city",
      type: "text",
      label: "City",
      subtype: "address",
      placeholder: "City",
      required: true,
      maxLength: 40,
      showRequiredIndicator: true,
      validate: (schema: FieldSchema) => {
        schema.errors = errors.value?.city ? [errors.value.city] : [];
        schema.valid = schema.errors.length === 0;
        schema.touched = schema.errors.length > 0;
      },
    } as FieldSchema,
    stateId: {
      name: "stateId",
      type: "select",
      subtype: "address",
      label: "State",
      required: true,
      showRequiredIndicator: true,
      validate: (schema: FieldSchema) => {
        schema.errors = errors.value?.stateId ? [errors.value.stateId] : [];
        schema.valid = schema.errors.length === 0;
        schema.touched = schema.errors.length > 0;
      },
    } as FieldSchema,
    postalCode: {
      name: "postalCode",
      type: "zipcode",
      label: "Zip Code",
      placeholder: "Zip Code",
      errorMessage: "Zip code is required.",
      title: "Zip Code Format: XXXXX or XXXXX-XXXX or XXXXXXXXX",
      required: true,
      showRequiredIndicator: true,
      validate: (schema: FieldSchema) => {
        schema.errors = errors.value?.postalCode ? [errors.value.postalCode] : [];
        schema.valid = schema.errors.length === 0;
        schema.touched = schema.errors.length > 0;
      },
    } as FieldSchema,
    countryId: {
      name: "countryId",
      type: "select",
      label: "Country",
      errorMessage: "Country is required.",
      required: false,
      showRequiredIndicator: true,
      validate: (schema: FieldSchema) => {
        schema.errors = errors.value?.countryId ? [errors.value.countryId] : [];
        schema.valid = schema.errors.length === 0;
        schema.touched = schema.errors.length > 0;
      },
    } as FieldSchema,
  });

  const defaultAddress = ref<Address>({
    address1: "",
    address2: null as null | string,
    addressTypeId: AddressTypeIds.PermanentAddress,
    addressTypeName: null,
    addressUnitTypeId: null as null | number,
    addressUnitTypeName: null as null | string,
    applicantId: 0,
    id: 0,
    city: "",
    stateId: 0,
    stateName: "",
    postalCode: "",
    countryId: AddressCountryIds.USA,
    countryName: "",
  });

  const addressFormFields = computed(() => {
    const fields = [
      {
        name: addressSchema.value.address1.name,
        label: addressSchema.value.address1.label,
        type: addressSchema.value.address1.type,
        required: addressSchema.value.address1.required,
        value: data.address.value?.address1,
        width: "col-4",
        showOnAdd: true,
        showOnEdit: true,
        disabledOnEdit: false,
      },
      {
        name: addressSchema.value.addressUnitTypeId.name,
        label: addressSchema.value.addressUnitTypeId.label,
        type: addressSchema.value.addressUnitTypeId.type,
        required: addressSchema.value.addressUnitTypeId.required,
        value: data.address.value?.addressUnitTypeId,
        width: "col-4",
        showOnAdd: true,
        showOnEdit: true,
        disabledOnEdit: false,
        options: unitTypeOptions.value,
      },
      {
        name: addressSchema.value.address2.name,
        label: addressSchema.value.address2.label,
        type: addressSchema.value.address2.type,
        required: addressSchema.value.address2.required,
        value: data.address.value?.address2,
        width: "col-4",
        showOnAdd: true,
        showOnEdit: true,
        disabledOnEdit: false,
      },
      {
        name: addressSchema.value.city.name,
        label: addressSchema.value.city.label,
        type: addressSchema.value.city.type,
        required: addressSchema.value.city.required,
        value: data.address.value?.city,
        width: "col-4",
        showOnAdd: true,
        showOnEdit: true,
        disabledOnEdit: false,
      },
      {
        name: addressSchema.value.stateId.name,
        label: addressSchema.value.stateId.label,
        type: addressSchema.value.stateId.type,
        required: true,
        value: data.address.value?.stateId,
        width: "col-4",
        showOnAdd: true,
        showOnEdit: true,
        disabledOnEdit: false,
        options: statesOptions.value,
      },
      {
        name: addressSchema.value.postalCode.name,
        label: addressSchema.value.postalCode.label,
        type: addressSchema.value.postalCode.type,
        required: addressSchema.value.postalCode.required,
        value: data.address.value?.postalCode,
        title: "Zip Code Format: XXXXX or XXXXX-XXXX  or XXXXXXXXX",
        width: "col-4",
        showOnAdd: true,
        showOnEdit: true,
        disabledOnEdit: false,
      },
      {
        name: addressSchema.value.countryId.name,
        label: addressSchema.value.countryId.label,
        type: addressSchema.value.countryId.type,
        required: addressSchema.value.countryId.required,
        value: data.address.value?.countryId,
        width: "col-4",
        showOnAdd: false,
        showOnEdit: false,
        disabledOnEdit: false,
        options: countryOptions.value,
        visible: false,
      },
    ];

    return fields.filter(field => {
      if (data.mode.value === FormModes.add) {
        return field.showOnAdd !== false;
      } else if (data.mode.value === FormModes.edit) {
        return field.showOnEdit !== false;
      }

      return true;
    });
  });

  const addressValidationSchema = computed<yup.Schema>(() => {
    if (data.mode.value === FormModes.view) {
      return yup.object({});
    }

    return yup.object().shape({
      address1: yup
        .string()
        .label(addressSchema.value.address1?.label || "Permanent Street Address")
        .transform((curr, orig) => (orig === "" ? undefined : curr))
        .required(getErrorMessage(addressSchema.value.address1))
        .test(
          "valid-input",
          `${addressSchema.value.address1.label} can only contain letters, numbers, apostrophes, hyphens, and spaces.`,
          function (value) {
            // Only allow standard A-Z a-z 0-9 values, single space, and - '
            const valid = /^[A-Za-z0-9-' ]+$/.test(value);

            return valid === true;
          },
        ),
      addressUnitTypeId: yup
        .number()
        .label(addressSchema.value?.addressUnitTypeId?.label || "Unit Type")
        .transform(value => (isNaN(value) ? undefined : Number(value)))
        .test(
          "is-valid",
          getErrorMessage(addressSchema.value?.addressUnitTypeId),
          function (value, testContext) {
            const address2 = testContext.parent.address2;

            if (!address2) return true;

            if (
              address2 !== undefined &&
              address2 !== null &&
              (address2.toString() || "")?.trim()?.length > 0
            ) {
              return !isNaN(Number(value)) && Number(value) > 0;
            }

            return false;
          },
        ),
      address2: yup
        .string()
        .label(addressSchema.value.address2?.label || "Unit Number")
        .nullable()
        .transform((curr, orig) => (orig === "" ? undefined : curr))
        .test(
          "valid-unit-number",
          getErrorMessage(addressSchema.value.address2),
          function (value, testContext) {
            const unitType = testContext.parent.addressUnitTypeId;
            const hasInput = value !== undefined && value !== null && value?.trim().length > 0;

            if (!unitType && !hasInput) return true;

            if (
              hasInput ||
              (unitType !== undefined &&
                unitType !== null &&
                !isNaN(Number(unitType)) &&
                Number(unitType) > 0)
            ) {
              if (!hasInput) {
                return testContext.createError({
                  message: getErrorMessage(addressSchema.value.address2),
                });
              }
              // Only allow standard A-Z a-z 0-9 values and single space
              const specialCharacterCheck = /[^A-Za-z0-9\s]+$/.test(value ?? "");

              if (specialCharacterCheck) {
                return testContext.createError({
                  message: `${addressSchema.value.address2.label} can only contain letters, numbers, and spaces.`,
                });
              }

              return true;
            }

            return false;
          },
        ),
      city: yup
        .string()
        .label(addressSchema.value.city?.label || "City")
        .required(getErrorMessage(addressSchema.value.city))
        .max(40)
        .transform((curr, orig) => (orig === "" ? undefined : curr))
        .test(
          "valid-input",
          `${addressSchema.value.city.label} can only contain letters, apostrophes, hyphens, and spaces.`,
          function (value) {
            // Only allow standard A-Z a-z values, apostrophes, single space, and - '
            const invalidCharCheck = /[^A-Za-z-' ]/.test(value);
            return invalidCharCheck === false;
          },
        ),
      stateId: yup
        .number()
        .label(addressSchema.value.stateId?.label || "State")
        .transform(value => (isNaN(value) ? undefined : Number(value)))
        .required(getErrorMessage(addressSchema.value.stateId))
        .min(1, getErrorMessage(addressSchema.value.stateId)),
      postalCode: yup
        .string()
        .label(addressSchema.value.postalCode?.label || "Zip")
        .required(getErrorMessage(addressSchema.value.postalCode))
        .test(
          "is-valid-zip",
          "Please enter a valid ZIP code in 5-digit or 9-digit format (e.g., 12345, 12345-6789, or 123456789).",
          value => {
            // Handles 12345, 12345-6789
            const formatCheck1 = validZipFormat1Regex.test(value);
            // Handles 123456789
            const formatCheck2 = validZipFormat2Regex.test(value);

            if (value === undefined) {
              return false;
            } else if (value.length === 5 && formatCheck1) {
              return true;
            } else if (value.length === 10 && formatCheck1) {
              return true;
            } else if (value.length === 9 && formatCheck2) {
              return true;
            } else {
              return false;
            }
          },
        ),
    });
  });

  const hasErrors = computed<boolean>(() => errors.value && Object.keys(errors.value)?.length > 0);

  const formattedAddress2 = computed<string | null>(() => {
    if (selectedUnitType.value && selectedUnitType.value?.abbreviation && values.address2) {
      return `${selectedUnitType.value?.abbreviation} ${values.address2}`;
    }
    return null;
  });

  const selectedUnitType = computed<LOSType | undefined>(() =>
    addressUnitTypes.value.find(
      (t: LOSType) => t.id === Number(data.address.value?.addressUnitTypeId),
    ),
  );
  const { values, errors, setFieldValue, setValues, validateField } = useForm({
    validationSchema: addressValidationSchema,
  });

  function handleFieldChange(fieldName: string, value: any) {
    setFieldValue(fieldName, value);

    switch (fieldName) {
      case "addressUnitTypeId":
        data.address.value.addressUnitTypeName = getSelectedUnitTypeName(
          value,
          addressUnitTypes.value,
        );
        break;
      case "stateId":
        data.address.value.stateName = getOptionLabel(statesOptions.value, Number(value)) || "";
        break;
      case "countryId":
        data.address.value.countryName = getOptionLabel(countryOptions.value, Number(value)) || "";
        break;
      default:
        return;
    }
  }

  function resetSchemas() {
    for (const property in addressSchema.value) {
      addressSchema.value[property].valid = true;
      addressSchema.value[property].errors = [];
      addressSchema.value[property].touched = false;
    }
  }

  function discardChanges() {
    data.address.value = JSON.parse(JSON.stringify(addressBackup.value));
    addressBackup.value = JSON.parse(JSON.stringify(data.address.value));

    setAddress();
    resetSchemas();
  }

  function setAddress() {
    if (data.initialValues && data.initialValues.value) {
      addressBackup.value = JSON.parse(JSON.stringify(data.initialValues.value));
      data.address.value = {
        address1: data.initialValues.value?.address1 || "",
        address2: removePrefix(data.initialValues.value?.address2, prefixes.value) || null,
        addressId: data.initialValues.value?.id || 0,
        addressTypeId: data.initialValues.value?.addressTypeId || 1,
        addressTypeName: data.initialValues.value?.addressTypeName || "",
        addressUnitTypeId: data.initialValues.value?.addressUnitTypeId,
        addressUnitTypeName: data.initialValues.value?.addressUnitTypeName,
        applicantId: data.initialValues.value?.applicantId || 0,
        id: data.initialValues.value?.id || 0,
        city: data.initialValues.value?.city || "",
        stateId: data.initialValues.value?.stateId || 0,
        stateName: data.initialValues.value?.stateName || "",
        postalCode: data.initialValues.value?.postalCode
          ? formatZipCode(data.initialValues.value?.postalCode)
          : "",
        countryId: data.initialValues.value?.countryId || 0,
        countryName: data.initialValues.value?.countryName || "",
      };

      setValues(data.address.value);
    }
  }

  function setPrefixes() {
    prefixes.value = [];

    addressUnitTypes.value.forEach((au: any) => {
      prefixes.value.push(au?.abbreviation);
      prefixes.value.push(au?.name);
    });
  }

  onMounted(() => {
    setPrefixes();
  });
	
  return {
    addressBackup,
    addressFormFields,
    addressSchema,
    addressValidationSchema,
    addressUnitTypes,
    countryOptions,
    defaultAddress,
    discardChanges,
    errors,
    formatAddress2,
    formattedAddress2,
    formatZipCode,
    getSelectedUnitType,
    handleFieldChange,
    hasErrors,
    prefixes,
    removePrefix,
    resetSchemas,
    selectedUnitType,
    setAddress,
    setFieldValue,
    setPrefixes,
    setValues,
    statesOptions,
    values,
    validateField,
  };
}
